import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { config } from '../config/env'

// null default so useAuth genuinely throws outside AuthProvider
// (carried code used {} — truthy, which made the guard below dead code)
const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(() => localStorage.getItem('token'))
  const navigate = useNavigate()

  const validateToken = useCallback(async (tokenToValidate) => {
    try {
      const response = await fetch(config.api.auth.me, {
        headers: {
          'Authorization': `Bearer ${tokenToValidate}`
        }
      })

      if (response.ok) {
        const userData = await response.json()
        setUser(userData)
      } else {
        // Token is invalid, clear it
        localStorage.removeItem('token')
        setToken(null)
        setUser(null)
      }
    } catch (error) {
      console.error('Token validation error:', error)
      // Don't navigate on error, just clear auth state
      localStorage.removeItem('token')
      setToken(null)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Validate the persisted token once on mount. Login validates explicitly;
    // depending on `token` here double-fired validation on every login.
    if (token) {
      validateToken(token)
    } else if (import.meta.env.MODE === 'development' || import.meta.env.VITE_AUTH_BYPASS === '1') {
      // Local dev (vite `npm run dev`), or a build with VITE_AUTH_BYPASS=1 (the local
      // container): try the backend's DEV_AUTH_BYPASS so there is no login page. If the
      // backend isn't bypassing, /auth/me 401s and validateToken clears it, falling
      // through to login. Off by default — never fires in tests (MODE=test) or normal
      // prod builds (flag unset).
      localStorage.setItem('token', 'dev-local')
      setToken('dev-local')
      validateToken('dev-local')
    } else {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = async (username, password) => {
    try {
      // FastAPI OAuth2 expects form data, not JSON
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const response = await fetch(config.api.auth.token, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      })

      if (response.ok) {
        const data = await response.json()
        const accessToken = data.access_token

        localStorage.setItem('token', accessToken)
        setToken(accessToken)

        // Fetch user data after successful login
        await validateToken(accessToken)

        navigate('/dashboard')
        return { success: true }
      } else {
        const error = await response.json()
        return { success: false, error: error.detail || 'Login failed' }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, error: 'Network error' }
    }
  }

  const logout = useCallback(() => {
    localStorage.removeItem('token')
    setToken(null)
    setUser(null)
    navigate('/login')
  }, [navigate])

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}